import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { DataGrid } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import apiClient from '../ApiClient/ApiClient';
import BomActions from './BomActions';
import BomMainDataGrid from './BomMainDataGrid';

const emptyMain = { MainDrawNum: '', BuildDate: '', PreDays: 0 };
const emptyDetail = { OrderBy: '', DetailDrawNum: '', ItemName: '', DispenseAmount: 1, Chk: '' };

const apiError = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.Message || error?.message || fallback;

export default function Bom({ data }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(data?.boms || []);
  const [newData, setNewData] = useState(null);
  const [inputValue, setInputValue] = useState('58085400');
  const [selectedMainDrawNum, setSelectedMainDrawNum] = useState(null);
  const [currentHeader, setCurrentHeader] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [viewHistory, setViewHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [addingToExisting, setAddingToExisting] = useState(false);
  const [mainBom, setMainBom] = useState(emptyMain);
  const [detailBom, setDetailBom] = useState(emptyDetail);
  const [detailBoms, setDetailBoms] = useState([]);
  const [detailOptions, setDetailOptions] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [openSubDialog, setOpenSubDialog] = useState(false);
  const [subBomRows, setSubBomRows] = useState([]);

  useEffect(() => {
    if (data?.boms) setRows(data.boms);
  }, [data]);

  useEffect(() => {
    const warn = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const canLeave = () => !dirty || window.confirm('尚有未儲存的 BOM 變更，確定放棄並離開嗎？');

  const validateDetailRows = (details, createMode = false) => {
    if (!details.length) return 'BOM 至少要有一筆明細。';
    const normalized = details.map((row) => {
      const drawNum = String(createMode ? row.DetailDrawNum : row.detailDrawNum || '').trim();
      const itemName = String(createMode ? row.ItemName : row.itemName || '').trim();
      const order = Number(createMode ? row.OrderBy : row.orderBy);
      const amount = Number(createMode ? row.DispenseAmount : row.dispenseAmount);
      const chk = String(createMode ? row.Chk : row.chk || '').trim();
      return { drawNum, itemName, order, amount, chk };
    });
    const invalid = normalized.find(({ drawNum, itemName, order, amount, chk }) =>
      !drawNum || drawNum.length > 20 || !itemName || itemName.length > 30 ||
      !Number.isInteger(order) || order <= 0 || !Number.isFinite(amount) || amount <= 0 || chk.length > 2);
    if (invalid) {
      if (!invalid.drawNum) return '子件圖號為必填欄位。';
      if (invalid.drawNum.length > 20) return `子件圖號 ${invalid.drawNum} 最多 20 個字元。`;
      if (!invalid.itemName) return `子件 ${invalid.drawNum} 的物品名為必填欄位。`;
      if (invalid.itemName.length > 30) return `子件 ${invalid.drawNum} 的物品名最多 30 個字元。`;
      if (!Number.isInteger(invalid.order) || invalid.order <= 0) return `子件 ${invalid.drawNum} 的序號必須是大於 0 的整數。`;
      if (!Number.isFinite(invalid.amount) || invalid.amount <= 0) return `子件 ${invalid.drawNum} 的配賦量必須大於 0。`;
      return `子件 ${invalid.drawNum} 的 CHK 最多 2 個字元。`;
    }
    const duplicateDrawNum = normalized.find((row, index) => normalized.findIndex((other) => other.drawNum.toUpperCase() === row.drawNum.toUpperCase()) !== index);
    if (duplicateDrawNum) return `子件圖號不可重複：${duplicateDrawNum.drawNum}。`;
    const duplicateOrder = normalized.find((row, index) => normalized.findIndex((other) => other.order === row.order) !== index);
    if (duplicateOrder) return `明細序號不可重複：${duplicateOrder.order}。`;
    return '';
  };

  const loadDetails = async (mainDrawNum, header, remember = true) => {
    if (!canLeave()) return;
    setLoading(true);
    try {
      const response = await apiClient.get('/Bom/GetMainDetails', { params: { drawNum: mainDrawNum } });
      const details = response.data?.boms || [];
      if (remember && rows.length) {
        setViewHistory((history) => [{ rows, selectedMainDrawNum, currentHeader }, ...history].slice(0, 3));
      }
      setRows(details);
      setSelectedMainDrawNum(mainDrawNum);
      setCurrentHeader(header || details[0] || null);
      setDirty(false);
    } catch (error) {
      alert(apiError(error, '載入 BOM 明細失敗。'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!canLeave()) return;
    const drawNum = inputValue.trim();
    if (!drawNum) { alert('請輸入圖號。'); return; }
    setLoading(true);
    try {
      const response = await apiClient.get('/Bom/GetMain', { params: { drawNum } });
      if (!response.data?.bom) { alert('查無 BOM 資料。'); return; }
      setRows(response.data.boms || []);
      setNewData(response.data);
      setSelectedMainDrawNum(response.data.bom.mainDrawNum);
      setCurrentHeader(response.data.bom);
      setDirty(false);
    } catch (error) {
      alert(apiError(error, '搜尋 BOM 失敗。'));
    } finally {
      setLoading(false);
    }
  };

  const processRowUpdate = (newRow) => {
    const normalized = {
      ...newRow,
      orderBy: Number(newRow.orderBy),
      dispenseAmount: Number(newRow.dispenseAmount),
    };
    setRows((current) => current.map((row) => row.id === normalized.id ? normalized : row));
    setDirty(true);
    return normalized;
  };

  const handleHeaderChange = (header) => {
    setCurrentHeader(header);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!currentHeader || !selectedMainDrawNum) { alert('請先選擇要修改的 BOM 主件。'); return; }
    if (!dirty) { alert('目前沒有需要儲存的變更。'); return; }
    const detailError = validateDetailRows(rows);
    if (detailError) { alert(detailError); return; }
    const buildDate = String(currentHeader.buildDate || '').trim();
    if (buildDate.length > 7) { alert('建檔日期最多 7 個字元。'); return; }
    if (!Number.isInteger(Number(currentHeader.preDays)) || Number(currentHeader.preDays) < 0) {
      alert('提前天數必須是 0 以上的整數。'); return;
    }

    setLoading(true);
    try {
      const response = await apiClient.put('/BomCrud/Save', {
        header: currentHeader,
        details: rows.map((row) => ({ ...row, id: row.id < 0 ? 0 : row.id })),
      });
      const result = response.data?.data;
      setRows(result?.boms || rows);
      setDirty(false);
      alert(response.data?.message || 'BOM 變更已儲存。');
    } catch (error) {
      alert(apiError(error, 'BOM 儲存失敗。'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (!canLeave() || !viewHistory.length) return;
    const [last, ...rest] = viewHistory;
    setRows(last.rows);
    setSelectedMainDrawNum(last.selectedMainDrawNum);
    setCurrentHeader(last.currentHeader);
    setViewHistory(rest);
    setDirty(false);
  };

  const handleDelete = async (row) => {
    if (row.id < 0) {
      setRows((current) => current.filter((detail) => detail.id !== row.id));
      return;
    }
    if (dirty) { alert('請先儲存或放棄目前變更，再刪除明細。'); return; }
    const isLast = rows.length === 1;
    const message = isLast
      ? '這是此 BOM 最後一筆明細。刪除後整個主件將不再有 BOM 資料，確定刪除嗎？'
      : `確定刪除子件 ${row.detailDrawNum} 嗎？此動作無法復原。`;
    if (!window.confirm(message)) return;
    if (isLast && !window.confirm('請再次確認：真的要刪除最後一筆 BOM 明細嗎？')) return;

    setLoading(true);
    try {
      const response = await apiClient.delete(`/BomCrud/Detail/${row.id}`);
      setRows(response.data?.data?.details || []);
      if (response.data?.data?.wasLastDetail) {
        setSelectedMainDrawNum(null);
        setCurrentHeader(null);
      }
      alert(response.data?.message || '刪除完成。');
    } catch (error) {
      alert(apiError(error, '刪除 BOM 明細失敗。'));
    } finally {
      setLoading(false);
    }
  };

  const handleQueryUpperBom = async (drawNum) => {
    if (!canLeave()) return;
    const value = String(drawNum || '').trim();
    if (!value) { alert('請先輸入或選擇圖號。'); return; }
    setLoading(true);
    try {
      const response = await apiClient.get('/Bom/GetSubBoms', { params: { drawNum: value } });
      setSubBomRows(response.data?.boms || []);
      setOpenSubDialog(true);
    } catch (error) {
      alert(apiError(error, '查詢上階 BOM 失敗。'));
    } finally {
      setLoading(false);
    }
  };

  const resetCreate = () => {
    setMainBom(emptyMain);
    setDetailBom(emptyDetail);
    setDetailBoms([]);
    setDetailOptions([]);
    setSelectedItemId('');
  };

  const openAddDetail = () => {
    resetCreate();
    setAddingToExisting(true);
    setDetailBom({ ...emptyDetail, OrderBy: Math.max(0, ...rows.map((row) => Number(row.orderBy) || 0)) + 1 });
    setOpen(true);
  };

  const searchItemOptions = async () => {
    const drawNum = detailBom.DetailDrawNum.trim();
    if (!drawNum) { alert('請先輸入子件圖號。'); return; }
    setLoading(true);
    try {
      const response = await apiClient.get('/Bom/ItemOptions', { params: { drawNum } });
      const options = response.data?.data || [];
      setDetailOptions(options);
      setSelectedItemId('');
      if (!options.length) alert('料品表查無符合的子件圖號。');
    } catch (error) {
      setDetailOptions([]);
      alert(apiError(error, '查詢料品失敗。'));
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (event) => {
    const id = event.target.value;
    const selected = detailOptions.find((option) => option.id === id);
    setSelectedItemId(id);
    if (selected) {
      setDetailBom((current) => ({ ...current, DetailDrawNum: selected.圖號, ItemName: selected.物品名 }));
    }
  };

  const handleInsertDetail = () => {
    if (addingToExisting) {
      const candidate = {
        ...currentHeader,
        id: Math.min(0, ...rows.map((row) => Number(row.id))) - 1,
        mainDrawNum: selectedMainDrawNum,
        orderBy: Number(detailBom.OrderBy),
        detailDrawNum: detailBom.DetailDrawNum.trim(),
        itemName: detailBom.ItemName.trim(),
        dispenseAmount: Number(detailBom.DispenseAmount),
        chk: detailBom.Chk.trim(),
      };
      const error = validateDetailRows([...rows, candidate]);
      if (error) { alert(error); return; }
      if (candidate.detailDrawNum.toUpperCase() === selectedMainDrawNum.toUpperCase()) {
        alert('子件圖號不可與主件圖號相同。'); return;
      }
      setRows((current) => [...current, candidate]);
      setDirty(true);
      setOpen(false);
      resetCreate();
      return;
    }
    const candidate = {
      ...detailBom,
      OrderBy: Number(detailBom.OrderBy),
      DispenseAmount: Number(detailBom.DispenseAmount),
      id: uuidv4(),
    };
    const error = validateDetailRows([...detailBoms, candidate], true);
    if (error) { alert(error); return; }
    if (candidate.DetailDrawNum.trim().toUpperCase() === mainBom.MainDrawNum.trim().toUpperCase()) {
      alert('子件圖號不可與主件圖號相同。'); return;
    }
    setDetailBoms((current) => [...current, candidate]);
    setDetailBom({ ...emptyDetail, OrderBy: candidate.OrderBy + 1 });
    setDetailOptions([]);
    setSelectedItemId('');
  };

  const handleCreate = async () => {
    const mainDrawNum = mainBom.MainDrawNum.trim();
    const buildDate = mainBom.BuildDate.trim();
    if (!mainDrawNum) { alert('主件圖號為必填欄位。'); return; }
    if (mainDrawNum.length > 20) { alert('主件圖號最多 20 個字元。'); return; }
    if (!buildDate || buildDate.length > 7) { alert('建檔日期為必填，最多 7 個字元。'); return; }
    if (!Number.isInteger(Number(mainBom.PreDays)) || Number(mainBom.PreDays) < 0) {
      alert('提前天數必須是 0 以上的整數。'); return;
    }
    const error = validateDetailRows(detailBoms, true);
    if (error) { alert(error); return; }

    setLoading(true);
    try {
      const response = await apiClient.post('/BomCrud/Create', {
        mainDrawNum,
        buildDate,
        preDays: Number(mainBom.PreDays),
        details: detailBoms.map((detail) => ({
          OrderBy: detail.OrderBy,
          DetailDrawNum: detail.DetailDrawNum,
          ItemName: detail.ItemName,
          DispenseAmount: detail.DispenseAmount,
          Chk: detail.Chk,
        })),
      });
      const result = response.data?.data;
      setRows(result?.boms || []);
      setSelectedMainDrawNum(mainDrawNum);
      setCurrentHeader(result?.boms?.[0] || null);
      setDirty(false);
      setOpen(false);
      resetCreate();
      alert(response.data?.message || 'BOM 新增完成。');
      const refreshed = await apiClient.get('/Bom/GetMain', { params: { drawNum: mainDrawNum } });
      setNewData(refreshed.data);
      setCurrentHeader(refreshed.data?.bom || result?.boms?.[0] || null);
    } catch (error) {
      alert(apiError(error, 'BOM 新增失敗。'));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: 'orderBy', headerName: '序', width: 65, type: 'number', editable: true },
    { field: 'detailDrawNum', headerName: '子件圖號', minWidth: 145, flex: 1, editable: true },
    { field: 'itemName', headerName: '物品名', minWidth: 180, flex: 1.2, editable: true },
    { field: 'chk', headerName: 'CHK', width: 70, editable: true },
    { field: 'dispenseAmount', headerName: '配賦量', width: 95, type: 'number', editable: true },
    {
      field: 'nextLevel', headerName: '下一階', width: 90, sortable: false,
      renderCell: (params) => (
        <Button size="small" startIcon={<ArrowDownwardIcon />} onClick={() => loadDetails(params.row.detailDrawNum, null, true)}>
          查看
        </Button>
      ),
    },
    {
      field: 'actions', headerName: '刪除', width: 70, sortable: false,
      renderCell: (params) => <BomActions params={params} onDelete={handleDelete} />,
    },
  ];

  const createColumns = [
    { field: 'OrderBy', headerName: '序', width: 65 },
    { field: 'DetailDrawNum', headerName: '子件圖號', minWidth: 150, flex: 1 },
    { field: 'ItemName', headerName: '物品名', minWidth: 180, flex: 1.2 },
    { field: 'Chk', headerName: 'CHK', width: 70 },
    { field: 'DispenseAmount', headerName: '配賦量', width: 95 },
    {
      field: 'delete', headerName: '刪除', width: 70,
      renderCell: (params) => (
        <Button color="error" onClick={() => setDetailBoms((current) => current.filter((row) => row.id !== params.row.id))}>
          <DeleteForeverIcon />
        </Button>
      ),
    },
  ];

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box display="flex" alignItems="center" gap={2} sx={{ width: '95%', mx: 'auto', mt: 5, mb: 2 }}>
        <TextField required label="圖號" value={inputValue} onChange={(event) => setInputValue(event.target.value)} />
        <Button variant="contained" onClick={handleSearch}>搜尋</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetCreate(); setAddingToExisting(false); setOpen(true); }}>新增 BOM</Button>
        <Button variant="outlined" onClick={() => handleQueryUpperBom(selectedMainDrawNum || inputValue)}>查看上階 BOM</Button>
        {dirty && <Chip color="warning" label="有未儲存變更" />}
      </Box>

      <Box sx={{ width: '95%', mx: 'auto', mb: '6%', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '35% 63%' }, gap: 2 }}>
        <Paper elevation={2} sx={{ p: 2, height: 560, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ fontWeight: 700, mb: 1 }}>主件（單擊載入明細）</Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <BomMainDataGrid
              data={data}
              newData={newData}
              onMainDrawNumSelect={loadDetails}
              onHeaderChange={handleHeaderChange}
            />
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ p: 2, height: 560, display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Box sx={{ fontWeight: 700, flexGrow: 1 }}>
              明細 {selectedMainDrawNum ? `（主件：${selectedMainDrawNum}）` : ''}
            </Box>
            <Tooltip title="回到上一個 BOM">
              <span><Button size="small" startIcon={<UndoIcon />} disabled={!viewHistory.length} onClick={handleBack}>上一步</Button></span>
            </Tooltip>
            <Button variant="outlined" startIcon={<AddIcon />} disabled={!selectedMainDrawNum || !currentHeader || loading} onClick={openAddDetail}>新增子件</Button>
            <Button variant="contained" startIcon={<SaveIcon />} disabled={!dirty || loading} onClick={handleSave}>儲存變更</Button>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              editMode="row"
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={(error) => alert(error?.message || '明細格式錯誤')}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              pageSizeOptions={[10, 20]}
              rowHeight={34}
              disableRowSelectionOnClick
            />
          </Box>
        </Paper>
      </Box>

      <Dialog open={open} onClose={() => { setOpen(false); resetCreate(); }} maxWidth="lg" fullWidth>
        <DialogTitle>{addingToExisting ? `新增子件（主件：${selectedMainDrawNum}）` : '新增 BOM'}</DialogTitle>
        <DialogContent>
          {!addingToExisting && <>
          <Box sx={{ fontWeight: 700, mt: 1, mb: 1 }}>1. 主件資料</Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField fullWidth required label="主件圖號" inputProps={{ maxLength: 20 }} value={mainBom.MainDrawNum}
                onChange={(event) => setMainBom((current) => ({ ...current, MainDrawNum: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth required label="建檔日期（最多 7 碼）" inputProps={{ maxLength: 7 }} value={mainBom.BuildDate}
                onChange={(event) => setMainBom((current) => ({ ...current, BuildDate: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth required type="number" label="提前天數" inputProps={{ min: 0 }} value={mainBom.PreDays}
                onChange={(event) => setMainBom((current) => ({ ...current, PreDays: event.target.value }))} />
            </Grid>
          </Grid>
          </>}

          <Box sx={{ fontWeight: 700, mt: 3, mb: 1 }}>{addingToExisting ? '加入後請按「儲存變更」，才會寫入資料庫。' : '2. 新增子件'}</Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField fullWidth required type="number" label="序" inputProps={{ min: 1 }} value={detailBom.OrderBy}
                onChange={(event) => setDetailBom((current) => ({ ...current, OrderBy: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth required label="子件圖號" inputProps={{ maxLength: 20 }} value={detailBom.DetailDrawNum}
                onChange={(event) => setDetailBom((current) => ({ ...current, DetailDrawNum: event.target.value }))}
                onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); searchItemOptions(); } }} />
            </Grid>
            <Grid item xs={12} md={2}><Button fullWidth variant="outlined" onClick={searchItemOptions}>查詢料品</Button></Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>料品選擇</InputLabel>
                <Select value={selectedItemId} label="料品選擇" onChange={handleItemSelect}>
                  {detailOptions.map((option) => <MenuItem key={option.id} value={option.id}>{option.圖號}｜{option.物品名}｜{option.單位}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField fullWidth required label="物品名" inputProps={{ maxLength: 30 }} value={detailBom.ItemName}
                onChange={(event) => setDetailBom((current) => ({ ...current, ItemName: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth required type="number" label="配賦量" inputProps={{ min: 0, step: 'any' }} value={detailBom.DispenseAmount}
                onChange={(event) => setDetailBom((current) => ({ ...current, DispenseAmount: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="CHK" inputProps={{ maxLength: 2 }} value={detailBom.Chk}
                onChange={(event) => setDetailBom((current) => ({ ...current, Chk: event.target.value }))} />
            </Grid>
            {!addingToExisting && <Grid item xs={12} md={2}><Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={handleInsertDetail}>加入明細</Button></Grid>}
          </Grid>

          {!addingToExisting && <>
          <Box sx={{ fontWeight: 700, mt: 3, mb: 1 }}>3. 確認明細（至少一筆）</Box>
          <Box sx={{ height: 300 }}>
            <DataGrid rows={detailBoms} columns={createColumns} pageSizeOptions={[10]} disableRowSelectionOnClick />
          </Box>
          </>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpen(false); resetCreate(); }}>取消</Button>
          <Button variant="contained" disabled={loading} onClick={addingToExisting ? handleInsertDetail : handleCreate}>{addingToExisting ? '加入明細' : '建立 BOM'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openSubDialog} onClose={() => setOpenSubDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>上階 BOM</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 420 }}>
            <DataGrid rows={subBomRows} columns={[
              { field: 'mainDrawNum', headerName: '主件圖號', flex: 1 },
              { field: 'itemName', headerName: '物品名', flex: 1 },
              { field: 'id', headerName: 'ID', width: 90 },
            ]} pageSizeOptions={[10, 20]} />
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpenSubDialog(false)}>關閉</Button></DialogActions>
      </Dialog>
    </>
  );
}
